import { _ } from "../polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import SupportInfoWidget from "./import/SupportInfoWidget.js";

/**
 * Display `validationResults` as a warning box.
 *
 * @param {AlertCollector} spec.collector - An object capable of collecting validation results
 * @param {boolean} [spec.displayWarnings] - Toggle to show both errors and warnings or only errors
 * @param {SupportInfo} [supportInfo] - Contact information to display in case of an error
 */
export default class ValidationResultWidget extends Widget {
	constructor({ collector, displayWarnings = true, supportInfo } = {}) {
		super(...arguments);

		this._displayWarnings = displayWarnings;

		this._supportInfoWidget = new SupportInfoWidget({
			title: _("Contact support"),
			supportInfo,
		});

		this._validationResultsChangedBinding = undefined;

		if (collector) {
			this.setCollector(collector);
		}
	}

	setCollector(collector) {
		if (this._collector) {
			this._validationResultsChangedBinding.unbind();
		}

		this._collector = collector;

		this._validationResultsChangedBinding =
			this._collector.changed.register(this.update.bind(this));

		this.update();
	}

	renderContentOn(html) {
		if (!this._collector) {
			return;
		}

		const validationResults = this._collector.getResults({
			includeWarnings: this._displayWarnings,
		});

		if (validationResults.length === 0) {
			return;
		}

		html.div(
			{
				klass: [
					"alert",
					`alert-${
						this._collector.hasError() ? "danger" : "warning"
					}`,
				],
				"data-test": "validation-result",
			},
			this._validationResultsRenderer(validationResults),
			this._supportInfoWidget,
		);
	}

	_validationResultsRenderer(validationResults) {
		return (html) => {
			if (validationResults.length === 1) {
				html.render(
					this._descriptionRenderer(
						validationResults[0].getDescription(),
					),
				);
			} else {
				html.ul(
					validationResults.map((validationResult) =>
						this._validationResultRenderer(validationResult),
					),
				);
			}
		};
	}

	_validationResultRenderer(result) {
		return (html) => {
			html.li(this._descriptionRenderer(result.getDescription()));
		};
	}

	_descriptionRenderer(description) {
		return description;
	}
}
